import type { PeriodSpec } from '@contracts';
import { resolvePeriod } from '@/lib/period';

/**
 * Modo Objetivos de la Carrera del Día — config POR ESCENARIO (VG/Piélé, SB).
 * Persistencia local (no es dato de negocio): clave `race-goals-<escenario>`.
 * Cuando está activo, cada sucursal corre contra su META del período (en vez de
 * contra el líder). El front solo pinta; los montos siguen viniendo del contrato.
 */
export type GoalPeriod = 'diario' | 'semanal' | 'mensual' | 'semestral' | 'anual';

export interface GoalConfig {
  enabled: boolean;
  goals: Record<GoalPeriod, number>;
}

export const GOAL_PERIODS: { key: GoalPeriod; label: string; icon: string }[] = [
  { key: 'diario', label: 'Diario', icon: '☀️' },
  { key: 'semanal', label: 'Semanal', icon: '📅' },
  { key: 'mensual', label: 'Mensual', icon: '🗓️' },
  { key: 'semestral', label: 'Semestral', icon: '📊' },
  { key: 'anual', label: 'Anual', icon: '🎯' },
];

export const DEFAULT_GOALS: Record<GoalPeriod, number> = {
  diario: 50_000,
  semanal: 300_000,
  mensual: 1_000_000,
  semestral: 5_000_000,
  anual: 12_000_000,
};

const CANON_DAYS: Record<GoalPeriod, number> = {
  diario: 1, semanal: 7, mensual: 30, semestral: 182, anual: 365,
};

const keyOf = (scenario: string) => `race-goals-${scenario}`;

export function loadGoals(scenario: string): GoalConfig {
  try {
    const raw = localStorage.getItem(keyOf(scenario));
    if (raw) {
      const p = JSON.parse(raw) as Partial<GoalConfig>;
      return { enabled: !!p.enabled, goals: { ...DEFAULT_GOALS, ...(p.goals ?? {}) } };
    }
  } catch {
    /* localStorage no disponible o JSON corrupto → defaults */
  }
  return { enabled: false, goals: { ...DEFAULT_GOALS } };
}

export function saveGoals(scenario: string, cfg: GoalConfig): void {
  try {
    localStorage.setItem(keyOf(scenario), JSON.stringify(cfg));
  } catch {
    /* ignora si localStorage no está disponible */
  }
}

/** Mapea el período del dashboard al objetivo más cercano por duración (en días). */
export function periodToGoalPeriod(period: PeriodSpec): GoalPeriod {
  const { from, to } = resolvePeriod(period);
  const days = Math.max(1, Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86_400_000) + 1);
  let best: GoalPeriod = 'mensual';
  let bestDiff = Infinity;
  (Object.keys(CANON_DAYS) as GoalPeriod[]).forEach((k) => {
    const diff = Math.abs(CANON_DAYS[k] - days);
    if (diff < bestDiff) { bestDiff = diff; best = k; }
  });
  return best;
}
